import { sortBy } from 'lodash';
import fpKeyBy from 'lodash/fp/keyBy';
import type { DefaultRootState } from 'react-redux';

import type { SelectedAlert, Url } from '~/@types/redux';
import type { DtoV1NavigationGutterItem } from '~/__generated__/api';
import apiEndpoints from '~/common/constants/api-endpoints';
import { setLoanSummaryUpsertOrDelete } from '~/common/redux/actions';
import apiClient, { typedApiClient } from '~/common/util/api-client';
import { getNextCategoryAlert } from '~/common/util/ruleUtils';
import { CATEGORY_DETAILS } from '~/pages/Home/constants';
import { HomeActions } from '~/pages/Home/redux';

import actions from './actions';

/*
 *  handles navigation from 1003 to BOLT and determines the next alert when pressing 'continue'
 */

const handleNavigation = (
  isLoanSummaryNav,
  isBoltNav,
  allAlerts,
  selectedCategory,
  dispatch,
  isUnderwriter: boolean,
) => {
  if (isLoanSummaryNav) {
    const filteredAlerts = allAlerts.filter(
      (alert) => alert.ruleCategoryId === selectedCategory,
    );
    const alerts = filteredAlerts.length ? filteredAlerts : allAlerts;
    const nextAlert = getNextCategoryAlert(alerts);
    dispatch(HomeActions.setSelectedAlert(nextAlert));
    dispatch(HomeActions.setSelectedCategory(nextAlert.ruleCategoryId));
    dispatch(setLoanSummaryUpsertOrDelete(false));
  }

  if (isBoltNav) {
    dispatch(actions.setShouldGetNextAlert(false));
    dispatch(actions.nextAlert(isUnderwriter));
  }
};

const getNavigationDetails =
  (getNextAlert: boolean, isUnderwriter: boolean) =>
  async (
    dispatch,
    getState: () => DefaultRootState & {
      url: Url;
    },
  ) => {
    dispatch(actions.getNavigationDetailsStarted());
    const {
      url: { obfuscatedLoanIdentifier },
      loanSummaryUpsertOrDelete: { loanSummaryUpsertOrDelete },
      home,
    } = getState();

    try {
      const { data } = await typedApiClient.v1.loanGetNavigationDetails(
        obfuscatedLoanIdentifier,
      );

      const flattenToLeaves = (
        gutterItem: DtoV1NavigationGutterItem,
      ): DtoV1NavigationGutterItem[] =>
        gutterItem.gutterItemChildren?.length
          ? gutterItem.gutterItemChildren.flatMap(flattenToLeaves)
          : [gutterItem];

      // Group by the structural container category (matches the visible tab
      // order) rather than each leaf's own ruleCategoryId, which is not
      // guaranteed to match its container for nested items.
      const categoriesInTabOrder = sortBy(
        data?.topNavigationItems ?? [],
        (category) => {
          const categoryIndex = CATEGORY_DETAILS.findIndex(
            (details) => details.id === category?.navigationCategoryId,
          );
          return categoryIndex === -1 ? Number.MAX_SAFE_INTEGER : categoryIndex;
        },
      );

      const allAlerts = categoriesInTabOrder.flatMap((category) =>
        sortBy(
          (category?.navigationGutterItems ?? [])
            .flatMap(flattenToLeaves)
            .map((alert) => ({
              ...alert,
              navigationCategoryId: category.navigationCategoryId,
            })),
          ['parentRuleSortOrder'],
        ),
      );
      const hasAccessToUnderwriterView = data?.topNavigationItems?.some(
        (item) => item.canReadUnderwriterCheckboxes,
      );
      const canWriteUnderwriterCheckbox = data?.topNavigationItems?.some(
        (item) => item.canWriteUnderwriterCheckbox,
      );
      const canOverrideAmcCheckbox = data?.topNavigationItems?.some(
        (item) => item.canOverrideAMCCheckbox,
      );

      dispatch(
        HomeActions.setUnderWriterViewPermissions({
          hasAccessToUnderwriterView,
          canWriteUnderwriterCheckbox,
          canOverrideAmcCheckbox,
        }),
      );

      dispatch(
        actions.getNavigationDetailsSuccess({
          categories: data?.topNavigationItems,
          allAlerts,
          allRequiredRulesCompleted: data?.allRequiredRulesCompleted,
          loanLockInformation: data?.loanLockInformation,
        }),
      );

      handleNavigation(
        loanSummaryUpsertOrDelete,
        getNextAlert && home?.shouldGetNextAlert,
        allAlerts,
        home.selectedCategory,
        dispatch,
        isUnderwriter,
      );
    } catch {
      dispatch(actions.getNavigationDetailsError());
    }
  };

const triggerAllRules =
  () => async (dispatch, getState: () => DefaultRootState & { url: Url }) => {
    dispatch(actions.triggerAllRulesStarted());
    const {
      url: { obfuscatedLoanIdentifier },
    } = getState();
    try {
      await typedApiClient.v1.ruleTriggerAllRules({ obfuscatedLoanIdentifier });
      dispatch(actions.triggerAllRulesSuccess());
    } catch {
      dispatch(actions.triggerAllRulesError());
    }
  };

const getUnderwriterVerifications =
  () => async (dispatch, getState: () => DefaultRootState & { url: Url }) => {
    dispatch(actions.getUnderwriterVerificationStarted());
    const {
      url: { obfuscatedLoanIdentifier },
    } = getState();
    try {
      const { data } = await apiClient.post(
        apiEndpoints.GET_UNDERWRITER_VERIFICATION_STATUS,
        obfuscatedLoanIdentifier,
      );
      const keyedData = fpKeyBy('ruleResultID', data);
      for (const key in keyedData)
        keyedData[key].FEVerified = keyedData[key].isUnderwriterVerified;
      dispatch(actions.getUnderwriterVerificationSuccess(keyedData));
    } catch {
      dispatch(actions.getUnderwriterVerificationError());
    }
  };

const saveUnderwriterVerification =
  (isVerified: boolean, selectedAlert?: SelectedAlert) =>
  async (dispatch, getState: () => DefaultRootState & { url: Url }) => {
    dispatch(actions.saveUnderwriterVerificationStarted());
    const {
      url: { obfuscatedLoanIdentifier },
      home: {
        selectedAlert: { ruleResultId: selectedRuleId },
        selectedAlertParent,
      },
    } = getState();
    const parentRuleId = selectedAlertParent?.ruleResultId;
    try {
      const ruleId =
        selectedAlert?.ruleResultId || selectedRuleId || parentRuleId;

      await typedApiClient.v1.underwriterVerifiedSaveUnderwriterVerified({
        obfuscatedLoanIdentifier,
        ruleResultId: ruleId,
        isUnderwriterVerified: isVerified,
      });

      dispatch(
        actions.saveUnderwriterVerificationSuccess({
          ruleResultID: ruleId,
          isUnderwriterVerified: isVerified,
        }),
      );
    } catch {
      dispatch(actions.saveUnderwriterVerificationError());
    }
  };

export default {
  getNavigationDetails,
  triggerAllRules,
  getUnderwriterVerifications,
  saveUnderwriterVerification,
};
